import type {
  CreateAuditIssueDTO,
  CreateAuditPlanFormDTO,
  CreateAuditRecordDTO,
  UpdateAuditIssueDTO,
  UpdateAuditPlanFormDTO,
  UpdateAuditRecordDTO,
} from "@/modules/internal-audit/domain/contracts";
import type {
  CreateAuditTypeDTO,
  UpdateAuditTypeDTO,
} from "@/modules/internal-audit/domain/contracts/type";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  auditIssuesApi,
  auditPlanApi,
  auditRecordsApi,
  auditTypesApi,
} from "./client";

export const auditPlanKeys = {
  all: ["internal-audit"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["internal-audit", "list", q] as const,
  detail: (id: string) => ["internal-audit", "detail", id] as const,
  summary: (params?: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
  }) => ["internal-audit", "summary", params] as const,
};

export function useAuditPlansQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: auditPlanKeys.list(q),
    queryFn: () => auditPlanApi.list(q),
  });
}

export function useAuditPlanDetailQuery(id: string) {
  return useQuery({
    queryKey: auditPlanKeys.detail(id),
    queryFn: () => auditPlanApi.get(id),
    enabled: !!id,
  });
}

export function useAuditSummaryStatsQuery(params?: {
  startDate?: string;
  endDate?: string;
  departmentId?: string;
}) {
  return useQuery({
    queryKey: auditPlanKeys.summary(params),
    queryFn: () => auditPlanApi.getSummaryStats(params),
  });
}

export function useCreateAuditPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAuditPlanFormDTO) => auditPlanApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditPlanKeys.all });
      toast.success("ສ້າງຜະແນການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error: unknown) => {
      // Extract error message if available
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null && "message" in error
            ? String((error as { message: unknown }).message)
            : "ບໍ່ສາມາດສ້າງຜະແນການກວດສອບໄດ້";
      toast.error(errorMessage);
      console.error("Create audit plan error:", error);
    },
  });
}

export function useUpdateAuditPlan(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAuditPlanFormDTO) =>
      auditPlanApi.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditPlanKeys.all });
      queryClient.invalidateQueries({ queryKey: auditPlanKeys.detail(id) });
      toast.success("ອັບເດດຜະແນການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error("ບໍ່ສາມາດອັບເດດຜະແນການກວດສອບໄດ້");
      console.error(error);
    },
  });
}

export function useDeleteAuditPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => auditPlanApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditPlanKeys.all });
      toast.success("ລຶບຜະແນການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "ບໍ່ສາມາດລຶບຜະແນການກວດສອບໄດ້",
      );
      console.error(error);
    },
  });
}

// Audit Type Query Keys
export const auditTypeKeys = {
  all: ["audit-types"] as const,
  lists: () => [...auditTypeKeys.all, "list"] as const,
  list: (q: OffsetPageQueryDTO) => [...auditTypeKeys.lists(), q] as const,
  details: () => [...auditTypeKeys.all, "detail"] as const,
  detail: (id: string) => [...auditTypeKeys.details(), id] as const,
};

export function useAuditTypes(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: auditTypeKeys.list(query),
    queryFn: () => auditTypesApi.list(query),
  });
}

export function useAuditType(id: string) {
  return useQuery({
    queryKey: auditTypeKeys.detail(id),
    queryFn: () => auditTypesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAuditType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAuditTypeDTO) => auditTypesApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດການກວດສອບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: auditTypeKeys.all });
    },
    onError: () => toast.error("ສ້າງປະເພດການກວດສອບລົ້ມເຫຼວ"),
  });
}

export function useUpdateAuditType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAuditTypeDTO }) =>
      auditTypesApi.update(id, input),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂປະເພດການກວດສອບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: auditTypeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: auditTypeKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂປະເພດການກວດສອບລົ້ມເຫຼວ"),
  });
}

export function useDeleteAuditType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => auditTypesApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບປະເພດການກວດສອບສໍາເລັດ");
      qc.invalidateQueries({ queryKey: auditTypeKeys.all });
    },
    onError: () => toast.error("ລຶບປະເພດການກວດສອບລົ້ມເຫຼວ"),
  });
}

// Audit Record Query Keys
export const auditRecordKeys = {
  all: ["audit-records"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["audit-records", "list", q] as const,
  detail: (id: string) => ["audit-records", "detail", id] as const,
};

export function useAuditRecordsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: auditRecordKeys.list(q),
    queryFn: () => auditRecordsApi.list(q),
  });
}

export function useAuditRecordQuery(id: string) {
  return useQuery({
    queryKey: auditRecordKeys.detail(id),
    queryFn: () => auditRecordsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAuditRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAuditRecordDTO) => auditRecordsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditRecordKeys.all });
      toast.success("ສ້າງບັນທຶກການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error("ບໍ່ສາມາດສ້າງບັນທຶກການກວດສອບໄດ້");
      console.error(error);
    },
  });
}

export function useUpdateAuditRecord(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAuditRecordDTO) =>
      auditRecordsApi.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditRecordKeys.all });
      queryClient.invalidateQueries({ queryKey: auditRecordKeys.detail(id) });
      toast.success("ອັບເດດບັນທຶກການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error("ບໍ່ສາມາດອັບເດດບັນທຶກການກວດສອບໄດ້");
      console.error(error);
    },
  });
}

export function useDeleteAuditRecord() {
  const queryClient = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => auditRecordsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditRecordKeys.all });
      toast.success("ລຶບບັນທຶກການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: () => {
      toast.error("ບໍ່ສາມາດລຶບບັນທຶກການກວດສອບໄດ້");
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Audit Issue Query Keys
export const auditIssueKeys = {
  all: ["audit-issues"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["audit-issues", "list", q] as const,
  detail: (id: string) => ["audit-issues", "detail", id] as const,
  byRecordItem: (recordItemId: string) =>
    ["audit-issues", "by-record-item", recordItemId] as const,
};

export function useAuditIssuesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: auditIssueKeys.list(q),
    queryFn: () => auditIssuesApi.list(q),
  });
}

export function useAuditIssuesByRecordItemQuery(recordItemId: string) {
  return useQuery({
    queryKey: auditIssueKeys.byRecordItem(recordItemId),
    queryFn: () => auditIssuesApi.getByRecordItem(recordItemId),
    enabled: !!recordItemId,
  });
}

export function useAuditIssueQuery(id: string) {
  return useQuery({
    queryKey: auditIssueKeys.detail(id),
    queryFn: () => auditIssuesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAuditIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAuditIssueDTO) => auditIssuesApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.all });
      toast.success("ສ້າງບັນຫາການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error("ບໍ່ສາມາດສ້າງບັນຫາການກວດສອບໄດ້");
      console.error(error);
    },
  });
}

export function useUpdateAuditIssue(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAuditIssueDTO) =>
      auditIssuesApi.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.all });
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.detail(id) });
      toast.success("ອັບເດດບັນຫາການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: (error) => {
      toast.error("ບໍ່ສາມາດອັບເດດບັນຫາການກວດສອບໄດ້");
      console.error(error);
    },
  });
}

export function useDeleteAuditIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => auditIssuesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.all });
      toast.success("ລຶບບັນຫາການກວດສອບສຳເລັດແລ້ວ");
    },
    onError: () => {
      toast.error("ບໍ່ສາມາດລຶບບັນຫາການກວດສອບໄດ້");
    },
  });
}
